import type { NotificationPreference } from "@closer/shared";
import { PERMISSION_DOMAIN_NAMES } from "@closer/shared";
import { Badge, Card, Checkbox, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { resolveErrorState } from "../../lib/uiState";

/**
 * BİLDİRİM TERCİHLERİ (CLOS-187 `[169]`).
 *
 * ### ⚠ Neden Tercihler sayfasında, Bildirim Merkezinde değil
 *
 * Merkez bir OKUMA yüzeyi: kullanıcı oraya "ne olmuş?" diye gidiyor. Kanal ayarı ise bir
 * hesap tercihi ve kullanıcı onu dil/tema ile aynı yerde arıyor. Ayrıca tercih
 * çalışma alanına değil KİŞİYE ait — merkez ise aktif alanı gösteriyor; ikisini aynı
 * ekrana koymak, alan değiştirince tercihlerin de değiştiği izlenimini verirdi.
 *
 * ### ⚠ KAPATILAMAZ OLAY ÖNCEDEN SÖYLENİYOR
 *
 * Kartın kriteri *"zorunlu güvenlik bildirimleri kapatılamaz."* Sunucu her kategori için
 * `hasMandatory` dönüyor ve kapalı bir kategoride bu rozet görünüyor. Söylemeseydik
 * kullanıcı kapattığı kategoriden bildirim almaya devam eder ve kapatma sözünün
 * tutulmadığını düşünürdü — asıl kusur teslimin kendisi değil, verilmemiş bilgi olurdu.
 *
 * ### ⚠ İSTEK TEK KATEGORİ GÜNCELLİYOR
 *
 * Bütün kümeyi tek istekte gönderseydik, iki sekmeden yapılan iki değişiklikten ikincisi
 * birincisini sessizce ezerdi.
 */
export function NotificationPreferencesSection() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const sorgu = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: async () =>
      (await api<NotificationPreference[]>("/api/notifications/preferences")).data,
  });

  const kaydet = useMutation({
    mutationFn: (girdi: { category: string; inApp: boolean; email: boolean }) =>
      api("/api/notifications/preferences", { method: "PUT", body: JSON.stringify(girdi) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notification-preferences"] }),
    onError: (error) =>
      toast.error(error instanceof ApiRequestError ? error.message : "Tercih kaydedilemedi"),
  });

  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          error: {
            title: "Bildirim tercihleri yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-fg-heading">Bildirimler</h2>
          <p className="text-sm text-fg-muted">
            Hangi konularda uygulama içi ve e-posta bildirimi almak istediğinizi seçin.
          </p>
        </div>

        {sorgu.isPending ? (
          <div role="status" aria-label="Bildirim tercihleri yükleniyor">
            <Skeleton lines={4} />
          </div>
        ) : hataDurumu ? (
          <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
        ) : (
          <ul className="flex flex-col gap-3">
            {(sorgu.data ?? []).map((t) => (
              <li
                key={t.category}
                data-testid="bildirim-tercihi"
                className="flex flex-wrap items-center justify-between gap-3 border-b border-border-default pb-3 last:border-0 last:pb-0"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-fg-strong">
                    {PERMISSION_DOMAIN_NAMES[t.category]}
                  </span>
                  {t.hasMandatory ? (
                    <span className="flex items-center gap-2">
                      <Badge tone="warning">Zorunlu uyarı var</Badge>
                      <span className="text-xs text-fg-soft">
                        Güvenlikle ilgili bazı bildirimler kapatılamaz.
                      </span>
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-4">
                  <Checkbox
                    label="Uygulama içi"
                    checked={t.inApp}
                    disabled={kaydet.isPending}
                    onChange={(olay) =>
                      kaydet.mutate({
                        category: t.category,
                        inApp: olay.target.checked,
                        email: t.email,
                      })
                    }
                  />
                  <Checkbox
                    label="E-posta"
                    checked={t.email}
                    disabled={kaydet.isPending}
                    onChange={(olay) =>
                      kaydet.mutate({
                        category: t.category,
                        inApp: t.inApp,
                        email: olay.target.checked,
                      })
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
